/**
 * Extension Capabilities
 *
 * The capability-injection Babel plugin rewrites extensions' free references to
 * ambient browser globals (fetch, window, document, …) into imports from
 * `@peek/extension-capabilities`. The loader serves a PER-ORIGIN capabilities
 * module for that specifier, scoped to the origin the extension was loaded from.
 *
 * Defaults:
 * - `fetch` is allowed, but ONLY to the origin the extension was loaded from
 *   (relative URLs resolve against it; cross-origin requests throw).
 * - every other capability is denied unless the host opts in via `configure`.
 *
 * This is a guard-rail/contract for trusted extensions, NOT a security boundary —
 * code in the same realm can still reach `window` via a rendered DOM node
 * (`node.ownerDocument.defaultView`). Real isolation needs a separate realm.
 *
 * @example
 * import capabilities from '@peekops/peek-extensions-framework/runtime/capabilities';
 * capabilities.configure({ localStorage: scopedStorage });
 */
class Capabilities {
  // Module specifier extensions import (and the import-map/loader resolves).
  static #SPECIFIER = '@peek/extension-capabilities';

  // Must stay in sync with DENYLIST in build/capability-injection.cjs.
  static #NAMES = [
    'fetch',
    'XMLHttpRequest',
    'WebSocket',
    'EventSource',
    'window',
    'document',
    'globalThis',
    'self',
    'top',
    'parent',
    'frames',
    'localStorage',
    'sessionStorage',
    'indexedDB',
    'navigator'
  ];

  #nativeFetch =
    typeof globalThis.fetch === 'function'
      ? globalThis.fetch.bind(globalThis)
      : undefined;

  // Host-provided capability implementations by name.
  #overrides: Record<string, unknown> = {};

  get specifier(): string {
    return Capabilities.#SPECIFIER;
  }

  /**
   * Allow/override capabilities for extensions (host opt-in). Anything not
   * configured keeps its default (scoped `fetch`, everything else denied).
   */
  configure(overrides: Record<string, unknown>): void {
    Object.assign(this.#overrides, overrides);
  }

  /**
   * Build the capabilities module served to an extension loaded from `origin`.
   * A plain object with enumerable getters so the loader's `Object.keys`-based
   * re-export picks every capability up, and host overrides stay live.
   *
   * @param origin - The origin the extension was loaded from ('' if unknown)
   */
  buildModule(origin: string): Record<string, unknown> {
    return Capabilities.#NAMES.reduce(
      (mod, name) => {
        Object.defineProperty(mod, name, {
          enumerable: true,
          get: () => this.#resolve(name, origin)
        });

        return mod;
      },
      {} as Record<string, unknown>
    );
  }

  #resolve(name: string, origin: string): unknown {
    if (name in this.#overrides) {
      return this.#overrides[name];
    }

    if (name === 'fetch') {
      return origin ? this.#scopedFetch(origin) : this.#denied('fetch');
    }

    return this.#denied(name);
  }

  /** A value that throws a clear error on any use (call, construct, read). */
  #denied(name: string): unknown {
    const fail = (): never => {
      throw new Error(`'${name}' is not available to extensions`);
    };

    return new Proxy(function denied() {}, {
      apply: fail,
      construct: fail,
      get: fail,
      set: fail,
      has: fail
    });
  }

  /** A `fetch` that only reaches `origin` (relative URLs resolve against it). */
  #scopedFetch(origin: string): typeof fetch {
    const nativeFetch = this.#nativeFetch;

    return (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> => {
      if (!nativeFetch) {
        throw new Error('fetch is not available in this environment');
      }

      const raw =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input.url;

      const target = new URL(raw, origin);

      if (target.origin !== origin) {
        throw new Error(
          `Extension fetch blocked: only ${origin} is allowed (requested ${target.origin}).`
        );
      }

      return nativeFetch(target, init);
    };
  }
}

export default new Capabilities();
